import { MatSnackBar } from '@angular/material/snack-bar';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Turns whatever an API or error handler produced (HttpErrorResponse, validation arrays,
 * ASP.NET problem details, { Message }, Error, string) into text a user can read.
 */
export function toReadableErrorMessage(value: unknown, fallback = GENERIC_ERROR_MESSAGE): string {
  const messages = collectMessages(value, 0);
  const unique = [...new Set(messages.map((m) => m.trim()).filter(Boolean))];
  return unique.length ? unique.join('\n') : fallback;
}

function collectMessages(value: unknown, depth: number): string[] {
  if (value === null || value === undefined || depth > 4) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap((item) => collectMessages(item, depth + 1));
  if (typeof value !== 'object') return [];

  const obj = value as Record<string, any>;

  // HttpErrorResponse: prefer the server's body, then a status-based message.
  if (typeof obj['status'] === 'number' && 'error' in obj) {
    if (obj['status'] === 0) return ['Cannot reach the server. Check your connection and try again.'];
    const fromBody = collectMessages(obj['error'], depth + 1);
    return fromBody.length ? fromBody : [`Request failed (HTTP ${obj['status']}).`];
  }

  for (const key of ['ErrorMessage', 'errorMessage', 'Message', 'message', 'detail', 'Detail']) {
    if (typeof obj[key] === 'string' && obj[key].trim()) return [obj[key]];
  }

  // ASP.NET validation problem details: { title, errors: { Field: ['msg'] } }
  if (obj['errors'] && typeof obj['errors'] === 'object') {
    const fieldMessages = Array.isArray(obj['errors'])
      ? collectMessages(obj['errors'], depth + 1)
      : Object.values(obj['errors']).flatMap((v) => collectMessages(v, depth + 1));
    if (fieldMessages.length) return fieldMessages;
  }

  for (const key of ['error', 'Error', 'value', 'Value', 'title', 'Title']) {
    const nested = collectMessages(obj[key], depth + 1);
    if (nested.length) return nested;
  }
  return [];
}

/**
 * Many handlers pass raw API error payloads to MatSnackBar.open, which renders objects as
 * "[object Object]". Converting non-string messages here covers every call site, including
 * snack bar instances provided by lazy-loaded modules.
 */
export function installReadableSnackBarMessages(): void {
  const proto = MatSnackBar.prototype as MatSnackBar & { __readableMessages?: boolean };
  if (proto.__readableMessages) return;
  proto.__readableMessages = true;
  const originalOpen = proto.open;
  proto.open = function (this: MatSnackBar, message: any, action?: string, config?: any) {
    const text = typeof message === 'string' ? message : toReadableErrorMessage(message);
    return originalOpen.call(this, text, action, config);
  };
}
